import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 sm:pt-24 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <Card className="bg-gradient-card backdrop-blur-sm border-border/50 p-6 sm:p-8 shadow-card">
            <div className="space-y-8">
              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  At MCQ AI, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
                  disclose, and safeguard your information when you use our service.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Information We Collect</h2>
                <div className="space-y-3 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Personal Information</h3>
                    <p className="leading-relaxed">
                      When you register for an account, we collect your name, email address, and authentication credentials. 
                      This information is necessary to provide you with our services and personalize your experience.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Usage Data</h3>
                    <p className="leading-relaxed">
                      We automatically collect information about your interactions with our platform, including quiz topics, 
                      scores, time spent, and usage patterns. This helps us improve our services and provide better recommendations.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Quiz Content</h3>
                    <p className="leading-relaxed">
                      The topics and questions you generate are stored to enable features like quiz history, sharing, 
                      and progress tracking.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">How We Use Your Information</h2>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground leading-relaxed">
                  <li>To provide, operate, and maintain our services</li>
                  <li>To improve and personalize your experience</li>
                  <li>To communicate with you about updates and features</li>
                  <li>To monitor and analyze usage patterns</li>
                  <li>To detect and prevent fraud or abuse</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement industry-standard security measures to protect your personal information. Your data is 
                  encrypted in transit and at rest. We use secure authentication protocols and regularly update our 
                  security practices to protect against unauthorized access.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Data Sharing</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  We do not sell your personal information to third parties. We may share your information only in the 
                  following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground leading-relaxed">
                  <li>With your explicit consent</li>
                  <li>To comply with legal obligations or court orders</li>
                  <li>To protect our rights, privacy, safety, or property</li>
                  <li>With service providers who assist in operating our platform (under strict confidentiality agreements)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  You have the following rights regarding your personal data:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground leading-relaxed">
                  <li>Access and review your personal information</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your account and data</li>
                  <li>Export your data in a portable format</li>
                  <li>Opt-out of marketing communications</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Cookies and Tracking</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use cookies and similar technologies to maintain your session, remember your preferences, and 
                  analyze site usage. You can control cookie settings through your browser, but disabling cookies may 
                  affect functionality.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our service is not intended for children under 13 years of age. We do not knowingly collect personal 
                  information from children under 13. If you believe we have collected such information, please contact us 
                  immediately.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
                  new policy on this page and updating the "Last updated" date. Your continued use of the service after 
                  changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about this Privacy Policy or our data practices, please contact us through 
                  our Contact Us page or email us at privacy@mcqai.online.
                </p>
              </section>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
